import { useDealContext } from "../../../deal/context";
import { useState, useEffect } from "react";
import ClientProvider from "../../../../modules/ClientProvider";
import { Button, Dialog, TableBody, TableCell, TableHead, TableRow, TextField, Stack, TableSortLabel, Link } from "@mui/material";
import { Box } from "@mui/material";
import { AdvisorTable, ExecTabs, ExecTab, EmptyList } from "../../../company/styles/AdvisorStyles";
import { useAuth0 } from "@auth0/auth0-react";
import { useUserContext } from "../../../users/context";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link as LinkIcon, UserPlus } from "lucide-react";
import { useAdvisor } from "../../context/AdvisorContext";

interface AdvisorSuggestion {
    id: number;
    uuid: string;
    name: string;
    email: string;
    linkedin_url: string;
    company_name: string;
    job_title: string;
    company_website: string;
    company_linkedin_url: string;
    status: string;
    created_at: string;
    created_by: {
        uuid: string;
        name: string;
    };
    personal_note: string;
}

const statusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Invited', value: 'invited' },
    { label: 'Active', value: 'active' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Deleted', value: 'deleted' }
];


export const ReferralForm = ({ onSubmit, onCancel, size="medium" }: {
    onSubmit: () => void,
    onCancel: () => void,
    size?: "small" | "medium" | undefined
}) => {

    const { dealContext } = useDealContext();
    const { project } = dealContext;
    const { getAccessTokenSilently } = useAuth0();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        linkedin_url: '',
        company_name: '',
        job_title: '',
        personal_note: `I'm helping ${project.organization.name} as a light-touch advisor for ${project.name}. I think you'd be a great fit, too.`,
    });

    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        try {
            const advisorClient = ClientProvider.provideAdvisorClient();
            const accessToken = await getAccessTokenSilently();

            const { error } = await advisorClient.addAdvisorSuggestion(
                project.tag,
                formData,
                accessToken
            );

            if (error) {
                setError(error.message);
                return;
            }

            setFormData({
                name: '',
                email: '',
                linkedin_url: '',
                company_name: '',
                job_title: '',
                personal_note: `I'm helping ${project.organization.name} as a light-touch advisor for ${project.name}. I think you'd be a great fit, too.`,
            });
            onSubmit();
        } catch (err) {
            setError('Failed to submit suggestion. Please try again.');
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            email: '',
            linkedin_url: '',
            company_name: '',
            job_title: '',
            personal_note: `I'm helping ${project.organization.name} as a light-touch advisor for ${project.name}. I think you'd be a great fit, too.`,
        });
        setError(null);
    };

    return (
            <Box sx={{ width: "100%" }}>
                {error && (
                    <Box sx={{ color: 'error.main', mb: 2 }}>{error}</Box>
                )}
                <form onSubmit={handleSubmit}>
                    <Stack direction="column" spacing={2}>
                        <TextField
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            size={size}
                            required
                        />
                        <TextField
                            label="Email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            size={size}
                            required
                        />
                        <TextField
                            label="LinkedIn URL"
                            name="linkedin_url"
                            value={formData.linkedin_url}
                            onChange={handleChange}
                            size={size}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField
                                label="Job Title"
                                name="job_title"
                                value={formData.job_title}
                                onChange={handleChange}
                                size={size}
                        />
                        <TextField
                            label="Company Name"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            size={size}
                        />
                        </Box>
                        <TextField
                            label="Personal Note"
                            name="personal_note"
                            multiline
                            rows={size === "small" ? 2 : 4}
                            value={formData.personal_note}
                            onChange={handleChange}
                            size={size}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                            <Button variant="outlined" onClick={onCancel}>Cancel</Button>
                            <Button variant="contained" type="submit">Submit</Button>
                        </Box>
                    </Stack>
                </form>
            </Box>
    );
};
